import { RaceEntry } from './RaceEntry.js';

/**
 * Resume — the top-level domain model for a runner's resume.
 *
 * Pure domain model, no Firebase dependencies.
 */
export class Resume {
  constructor({
    id = null,
    userId = null,
    name,
    subtitle = null,
    profileImageUrl = null,
    templateId = 'classic',
    isPublished = false,
    publicSettings = {},
    races = [],
    createdAt = null,
    updatedAt = null,
  } = {}) {
    this.id = id;
    this.userId = userId;
    this.name = name;
    this.subtitle = subtitle;
    this.profileImageUrl = profileImageUrl;
    this.templateId = templateId;
    this.isPublished = isPublished;
    this.publicSettings = publicSettings;
    this.races = races; // array of RaceEntry instances
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
  }

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  static validate(data) {
    const errors = [];

    if (!data.name || typeof data.name !== 'string' || data.name.trim() === '') {
      errors.push('name is required');
    } else if (data.name.length > 100) {
      errors.push('name must be 100 characters or less');
    }

    if (data.subtitle && typeof data.subtitle === 'string' && data.subtitle.length > 200) {
      errors.push('subtitle must be 200 characters or less');
    }

    if (data.profileImageUrl && typeof data.profileImageUrl === 'string') {
      try {
        const url = new URL(data.profileImageUrl);
        if (!['http:', 'https:'].includes(url.protocol)) {
          errors.push('profileImageUrl must use https');
        }
      } catch {
        errors.push('profileImageUrl must be a valid URL');
      }
    }

    if (data.races && Array.isArray(data.races)) {
      if (data.races.length > 200) {
        errors.push('maximum 200 races allowed');
      }
      data.races.forEach((race, i) => {
        const result = RaceEntry.validate(race);
        if (!result.valid) {
          result.errors.forEach((err) => errors.push(`races[${i}]: ${err}`));
        }
      });
    }

    return { valid: errors.length === 0, errors };
  }

  // ---------------------------------------------------------------------------
  // Firestore serialization
  // ---------------------------------------------------------------------------

  toFirestore() {
    return {
      userId: this.userId,
      name: this.name,
      subtitle: this.subtitle,
      profileImageUrl: this.profileImageUrl,
      templateId: this.templateId,
      isPublished: this.isPublished,
      publicSettings: this.publicSettings,
      races: this.races.map((r) => (r instanceof RaceEntry ? r.toFirestore() : r)),
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    };
  }

  static fromFirestore(id, data) {
    return new Resume({
      id,
      userId: data.userId ?? null,
      name: data.name,
      subtitle: data.subtitle ?? null,
      profileImageUrl: data.profileImageUrl ?? null,
      templateId: data.templateId ?? 'classic',
      isPublished: data.isPublished ?? false,
      publicSettings: data.publicSettings ?? {},
      races: (data.races ?? []).map((r) => RaceEntry.fromFirestore(r)),
      createdAt: data.createdAt ?? null,
      updatedAt: data.updatedAt ?? null,
    });
  }
}
